import { FC, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";

import {
  Claim,
  ClaimPayer,
  EdiObject,
  FeeChange,
  ManualPaymentFields,
  ManualPaymentOutcome,
  PayerTier,
  RecordManualPaymentInput,
} from "../../../../types/claims";
import { apiService } from "../../../../services/apiService";
import useNotifier from "../../../../hooks/useNotifier";
import {
  ClaimEditError,
  browserTimezone,
  claimDocumentError,
  defaultPayerTier,
  latestAppealDate,
  payerName,
} from "./claimAppeals";
import {
  ManualPaymentFieldErrors,
  ManualPaymentFormField,
  RecordPaymentErrorCode,
  hasPrimaryRemit,
  parseRecordPaymentError,
  validateManualPaymentForm,
} from "./record-payment/manualPayments";
import RecordPaymentForm from "./record-payment/RecordPaymentForm";
import RecordPaymentConfirm from "./record-payment/RecordPaymentConfirm";
import { EditErrorAlert } from "./record-payment/ClaimEditDialogParts";

interface RecordPaymentDialogProps {
  open: boolean;
  claim: Claim;
  ediObjects: EdiObject[];
  /** The claim's payer levels from the claim detail (REV-2621). */
  payers?: ClaimPayer[];
  onClose: () => void;
  /** Called once the payment is saved, or when the API says it already was
   * (duplicate_payment), so the caller refreshes the claim either way. */
  onRecorded: () => void;
}

type Step = "form" | "confirm";

// The name the claim has for a payer level; the claim's payer when it has none
const prefilledPayerName = (
  tier: PayerTier,
  claim: Claim,
  payers?: ClaimPayer[],
): string => payerName(tier, payers) || (tier === "primary" ? claim.payer || "" : "");

const emptyFields = (
  claim: Claim,
  payers?: ClaimPayer[],
): ManualPaymentFields => {
  const payerTier = defaultPayerTier(claim.status, payers);
  return {
    outcome: "paid",
    payerTier,
    amount: "",
    paymentDate: "",
    payerName: prefilledPayerName(payerTier, claim, payers),
    checkOrEftNumber: "",
    payerClaimNumber: "",
    patientResponsibility: "",
  };
};

/**
 * Admin-only: records a payment or denial a payer sent by letter, in two steps.
 * The form checks the API's rules as it is filled in; the summary shows the
 * effect on the claim and nothing is saved until it is confirmed.
 */
const RecordPaymentDialog: FC<RecordPaymentDialogProps> = ({
  open,
  claim,
  ediObjects,
  payers,
  onClose,
  onRecorded,
}) => {
  const theme = useTheme();
  const notifier = useNotifier();

  // The API caps the payment date at today in US/Eastern too
  const today = latestAppealDate();
  const primaryRemit = hasPrimaryRemit(ediObjects);

  const [step, setStep] = useState<Step>("form");
  const [fields, setFields] = useState<ManualPaymentFields>(() =>
    emptyFields(claim, payers),
  );
  const [document, setDocument] = useState<File | null>(null);
  const [documentError, setDocumentError] = useState<string | undefined>();
  // Field errors stay hidden until the first Review, then follow the form live
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ManualPaymentFieldErrors>(
    {},
  );
  const [saveError, setSaveError] = useState<ClaimEditError<
    RecordPaymentErrorCode,
    ManualPaymentFormField
  > | null>(null);
  // A server error with no field to show it on
  const [formError, setFormError] = useState<string | null>(null);
  const [fee, setFee] = useState<FeeChange | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Start fresh each time the dialog opens, or when the claim's payers load
  useEffect(() => {
    if (!open) return;
    setStep("form");
    setFields(emptyFields(claim, payers));
    setDocument(null);
    setDocumentError(undefined);
    setShowErrors(false);
    setServerErrors({});
    setSaveError(null);
    setFormError(null);
    setFee(null);
    setIsPreviewing(false);
    setIsSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payers]);

  const liveErrors = validateManualPaymentForm(fields, {
    today,
    primaryRemit,
  });
  const fieldErrors: ManualPaymentFieldErrors = {
    ...(showErrors ? liveErrors : {}),
    ...serverErrors,
    ...(documentError ? { document: documentError } : {}),
  };
  const isPaid = fields.outcome === "paid";

  const clearServerError = (field: ManualPaymentFormField) =>
    setServerErrors((errors) => {
      if (!errors[field]) return errors;
      const next = { ...errors };
      delete next[field];
      return next;
    });

  const setField = <K extends keyof ManualPaymentFields>(
    key: K,
    value: ManualPaymentFields[K],
  ) => {
    setFields((current) => ({ ...current, [key]: value }));
    clearServerError(key);
    setFormError(null);
  };

  const handlePayerTierChange = (tier: PayerTier) => {
    // The name follows the level until the admin types their own
    setFields((current) => ({
      ...current,
      payerTier: tier,
      payerName:
        current.payerName.trim() ===
        prefilledPayerName(current.payerTier, claim, payers)
          ? prefilledPayerName(tier, claim, payers)
          : current.payerName,
    }));
    clearServerError("payerTier");
    clearServerError("payerName");
    setFormError(null);
  };

  const handleOutcomeChange = (outcome: ManualPaymentOutcome) => {
    // A denial is $0 with no patient responsibility, so those are set for the admin
    setFields((current) => ({
      ...current,
      outcome,
      amount: outcome === "denied" ? "0.00" : "",
      patientResponsibility:
        outcome === "denied" ? "" : current.patientResponsibility,
    }));
    setServerErrors({});
  };

  const handleFiles = (files: File[]) => {
    const [file] = files;
    if (!file) return;
    const error = claimDocumentError(file);
    setDocumentError(error ?? undefined);
    clearServerError("document");
    if (!error) setDocument(file);
  };

  const buildInput = (): RecordManualPaymentInput => ({
    ...fields,
    amount: fields.amount.trim(),
    payerName: fields.payerName.trim(),
    checkOrEftNumber: fields.checkOrEftNumber.trim(),
    payerClaimNumber: fields.payerClaimNumber.trim(),
    patientResponsibility: fields.patientResponsibility.trim(),
    timezone: browserTimezone(),
  });

  const showValidationError = (
    error: ClaimEditError<RecordPaymentErrorCode, ManualPaymentFormField>,
  ) => {
    setSaveError(null);
    // The form has no timezone input, so that one is shown above the form
    const { timezone, ...shown } = error.fieldErrors;
    setServerErrors(shown);
    if (timezone || !Object.keys(shown).length) setFormError(error.message);
    setStep("form");
  };

  // Review previews the effect on the claim (REV-2584); nothing is saved yet
  const handleReview = async () => {
    setShowErrors(true);
    if (Object.keys(liveErrors).length || documentError) return;
    setFormError(null);
    setFee(null);
    setIsPreviewing(true);
    try {
      const response = await apiService.previewClaimManualPayment(
        claim.id,
        buildInput(),
      );
      if (response.success && response.data) {
        setSaveError(null);
        setFee(response.data);
        setStep("confirm");
        return;
      }
      const error = parseRecordPaymentError(response);
      if (error.code === "validation_error") {
        showValidationError(error);
        return;
      }
      // claim_invoiced and duplicate_payment block saving; any other failure
      // still lets the admin save without the preview
      setSaveError(error);
      setStep("confirm");
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await apiService.recordClaimManualPayment(
        claim.id,
        buildInput(),
        document,
      );
      if (response.success && response.data) {
        notifier.showSuccess(isPaid ? "Remit recorded." : "Denial recorded.");
        onRecorded();
        onClose();
        return;
      }
      const error = parseRecordPaymentError(response);
      if (error.code === "validation_error") {
        showValidationError(error);
        return;
      }
      setSaveError(error);
      // Already saved (a double submit): show it on the timeline
      if (error.code === "duplicate_payment") onRecorded();
    } finally {
      setIsSaving(false);
    }
  };

  // A late preview or save response would land on a closed dialog
  const handleClose = () => {
    if (isSaving || isPreviewing) return;
    onClose();
  };

  const isBlocked =
    saveError?.code === "claim_invoiced" ||
    saveError?.code === "duplicate_payment";

  const title = isPaid ? "payment" : "denial";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        {step === "form" ? "Record remit" : `Confirm ${title}`}
        <Typography
          component="div"
          sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
        >
          Claim {claim.claimNumber}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {step === "form" && formError && (
          <EditErrorAlert message={formError} />
        )}
        {step === "form" ? (
          <RecordPaymentForm
            fields={fields}
            fieldErrors={fieldErrors}
            liveErrors={liveErrors}
            primaryRemit={primaryRemit}
            today={today}
            document={document}
            payers={payers}
            onOutcomeChange={handleOutcomeChange}
            onPayerTierChange={handlePayerTierChange}
            onFieldChange={setField}
            onFiles={handleFiles}
            onRemoveDocument={() => setDocument(null)}
          />
        ) : (
          <RecordPaymentConfirm
            claim={claim}
            fields={fields}
            document={document}
            fee={fee}
            saveError={saveError?.message}
            onRetry={!isBlocked && !fee ? handleReview : undefined}
            isRetrying={isPreviewing}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {step === "form" ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isPreviewing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              variant="contained"
              loading={isPreviewing}
            >
              Review
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                setStep("form");
                setSaveError(null);
              }}
              variant="outlined"
              disabled={isSaving || isPreviewing}
            >
              Back
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={isSaving || isPreviewing || isBlocked}
              loading={isSaving}
            >
              Confirm and save
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RecordPaymentDialog;
